'use client'

import React from 'react'
import { cn, openInNewTab } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'

// ---------------------------------------------------------------------------
// NavigationCard
//
// Rendered below an AI message bubble when the AI calls navigate_to_entity.
// Styled to exactly match BriefingDownloadCard — a branded horizontal row
// with icon bubble, label + subtitle, and external-link arrow.
// Opens in a new tab so the conversation stays persistent.
// ---------------------------------------------------------------------------

export interface NavIntent {
  path: string
  label: string
  entity_type?: string
}

interface NavigationCardProps {
  navIntent: NavIntent
}

// Entity type → icon
function getEntityIcon(entityType?: string): React.ElementType {
  switch (entityType) {
    case 'claim':        return Icons.fileText
    case 'sample':       return Icons.layers
    case 'rfi':          return Icons.alertTriangle
    case 'da_worksheet': return Icons.pencil
    case 'rebuttal':     return Icons.repeat
    case 'dispute':      return Icons.shield
    case 'universe':     return Icons.inbox
    case 'workbench':    return Icons.workbench
    case 'dashboard':    return Icons.dashboard
    case 'reports':      return Icons.barChart
    case 'settings':     return Icons.settings
    case 'insights':     return Icons.brain
    case 'policies':     return Icons.lightbulb
    default:             return Icons.externalLink
  }
}

// Entity type → human-readable type label
function getTypeLabel(entityType?: string): string {
  if (!entityType) return 'Page'
  return entityType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export function NavigationCard({ navIntent }: NavigationCardProps) {
  const [opened, setOpened] = React.useState(false)

  const EntityIcon = getEntityIcon(navIntent.entity_type)
  const typeLabel = getTypeLabel(navIntent.entity_type)

  const handleOpen = () => {
    // navIntent.path is AI-generated (navigate_to_entity tool call) — validate
    // before it reaches the window.open sink.
    openInNewTab(navIntent.path)
    setOpened(true)
  }

  return (
    <button
      type="button"
      onClick={handleOpen}
      id={`nav-card-${navIntent.path.replace(/\W/g, '-')}`}
      className={cn(
        // Same row layout as BriefingDownloadCard
        'flex items-center gap-3 px-4 py-3 mt-2 rounded-xl w-full text-left',
        'bg-gradient-to-r from-brand-cornflower/10 via-brand-purple/5 to-transparent',
        'border border-brand-cornflower/25',
        'hover:from-brand-cornflower/15 hover:via-brand-purple/10',
        'hover:border-brand-cornflower/40',
        'transition-all duration-200 cursor-pointer',
        'group',
        opened && 'border-emerald-300/40',
      )}
      aria-label={`Open ${navIntent.label} in new tab`}
    >
      {/* Icon bubble — same as briefing card */}
      <div className={cn(
        'flex items-center justify-center w-9 h-9 rounded-lg shrink-0',
        'bg-gradient-to-br from-brand-navy to-brand-purple',
        'shadow-sm group-hover:shadow-md transition-shadow',
      )}>
        {opened ? (
          <Icons.checkCircle className="h-4 w-4 text-white" strokeWidth={1.5} />
        ) : (
          <EntityIcon className="h-4 w-4 text-white" strokeWidth={1.5} />
        )}
      </div>

      {/* Label + subtitle */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground">
          {typeLabel} — {navIntent.label}
        </div>
        <div className={cn(
          'text-xs',
          opened ? 'text-emerald-600' : 'text-muted-foreground',
        )}>
          {opened ? 'Opened in new tab ✓' : 'Click to navigate — opens in a new tab'}
        </div>
      </div>

      {/* External link arrow */}
      <Icons.externalLink
        className="h-4 w-4 text-brand-cornflower/60 group-hover:text-brand-cornflower transition-colors shrink-0"
        strokeWidth={1.5}
      />
    </button>
  )
}
